import { useEffect, useState } from 'react';
import type { JustificatifEntrant } from '../../types/ndf';
import { apercuDe } from './photo';

/**
 * L'attente pendant que l'IA lit le justificatif.
 *
 * Une lecture prend cinq à vingt secondes — trop long pour un écran figé, assez
 * court pour qu'une barre de progression mente. On montre donc la photo qui est
 * en train d'être lue et ce qui se passe, étape par étape : le salarié voit que
 * SON ticket est traité, pas un rouage abstrait.
 *
 * Les étapes défilent au temps, sans rien connaître de l'avancement réel : il
 * n'y en a pas à connaître, l'appel est atomique. Elles s'arrêtent sur la
 * dernière plutôt que de boucler — repartir à zéro laisserait croire à un
 * blocage.
 */

const ETAPES = [
  'Préparation du justificatif…',
  'Lecture du document…',
  'Reconnaissance des montants et de la date…',
  'Encore quelques instants…',
];

export function AnalyseEnCours({ piece }: { piece: JustificatifEntrant | undefined }) {
  const [etape, setEtape] = useState(0);

  useEffect(() => {
    const t = setInterval(() => setEtape((e) => Math.min(e + 1, ETAPES.length - 1)), 4000);
    return () => clearInterval(t);
  }, []);

  return (
    <div className="flex flex-col items-center gap-4 rounded-lg border border-nuit/10 bg-white p-8 text-center">
      <div className="relative">
        {piece && piece.mime.startsWith('image/') ? (
          <img
            src={apercuDe(piece)}
            alt=""
            className="h-32 w-32 rounded-lg object-cover opacity-70"
          />
        ) : (
          <span className="flex h-32 w-32 items-center justify-center rounded-lg bg-ciel/30 text-sm text-nuit">
            PDF
          </span>
        )}
        {/* Balayage clair de haut en bas : l'image d'un document qu'on scanne. */}
        <span className="pointer-events-none absolute inset-0 overflow-hidden rounded-lg">
          <span className="absolute inset-x-0 h-1/3 animate-balayage bg-gradient-to-b from-transparent via-white/70 to-transparent" />
        </span>
      </div>

      <p className="font-titre font-bold text-nuit" aria-live="polite">
        {ETAPES[etape]}
      </p>
      <p className="max-w-xs text-sm text-nuit/60">
        Vous pourrez tout corriger : les champs sont remplis à titre de proposition.
      </p>
    </div>
  );
}
